import { useEffect, useRef } from "react";
import { gsap, motionOK } from "../hooks/useSmoothScroll";
import { useLang } from "../hooks/useLanguage";
import { Reveal } from "./Reveal";

const HowItWorks = () => {
  const ref = useRef(null);
  const { t, dir } = useLang();

  useEffect(() => {
    if (!motionOK()) return undefined;
    const origin = dir === "rtl" ? "right center" : "left center";
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hiw-line-h",
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: "none",
          transformOrigin: origin,
          scrollTrigger: { trigger: ref.current, start: "top 70%", end: "bottom 55%", scrub: true },
        }
      );
      gsap.fromTo(
        ".hiw-line-v",
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          transformOrigin: "top center",
          scrollTrigger: { trigger: ref.current, start: "top 70%", end: "bottom 55%", scrub: true },
        }
      );
      gsap.utils.toArray(".hiw-step").forEach((step) => {
        gsap.fromTo(
          step,
          { opacity: 0.15, y: 30 },
          {
            opacity: 1,
            y: 0,
            ease: "none",
            scrollTrigger: { trigger: step, start: "top 94%", end: "top 62%", scrub: true },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, [dir]);

  return (
    <section ref={ref} id="how-it-works" className="w-full bg-white text-[#202020] py-24 md:py-32" style={{ backgroundColor: "#FFFFFF" }} data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal className="max-w-3xl">
          <p className="label-caps">{t.UI.hiwLabel}</p>
          <h2 className="mt-4 font-serif-lux font-light text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.08] tracking-tight">
            {t.UI.hiwTitle}
          </h2>
        </Reveal>

        <div className="relative mt-16 md:mt-24">
          <span className="hidden md:block absolute top-[1.7rem] start-0 end-0 h-px bg-[#141414]/12" />
          <span className="hiw-line-h hidden md:block absolute top-[1.7rem] start-0 end-0 h-px bg-champagne" />
          <span className="md:hidden absolute start-[0.8rem] top-2 bottom-2 w-px bg-[#141414]/12" />
          <span className="hiw-line-v md:hidden absolute start-[0.8rem] top-2 bottom-2 w-px bg-champagne" />

          <div className="grid md:grid-cols-4 gap-12 md:gap-10">
            {t.STEPS.map((s, i) => (
              <div key={s.n} className="hiw-step relative ps-12 md:ps-0 md:pt-20" data-testid={`how-step-${i + 1}`}>
                <span className="absolute start-[0.5rem] top-1 md:start-0 md:top-[1.4rem] h-[0.7rem] w-[0.7rem] rotate-45 bg-champagne" />
                <span className="latin-text font-serif-lux text-5xl font-light text-champagne">{s.n}</span>
                <h3 className="mt-4 font-serif-lux text-2xl font-medium">{s.title}</h3>
                <p className="mt-3 text-sm text-smoke leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
